import _ from 'lodash';
import { type JSX, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { QuestionnaireStatistics } from 'Components/UcrmTprm/QuestionnaireForm/Hooks/useQuestionnaireStatistics';
import { ICON_SUBMIT } from 'Config/Icons';

import styles from './QuestionnaireReviewSection.module.css';

export interface QuestionnaireReviewSectionProps {
    isSubmitted: boolean;
    /** Who is notified when the questionnaire is submitted, such as the client's name or "the assessment owner and delegates". Used mid-sentence. */
    notifiedParty: string;
    statistics: QuestionnaireStatistics;
    onSubmit: () => Promise<void>;

    /** If provided, the user is not allowed to submit the questionnaire, and this explanation is displayed instead of the submit button. */
    cannotSubmitReason?: string;
}

/** Renders the questionnaire section where the user can choose to submit the questionnaire (if all required questions have been answered). */
export const QuestionnaireReviewSection = (props: QuestionnaireReviewSectionProps): JSX.Element => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const canSubmit = props.statistics.unansweredRequiredQuestions === 0 && !props.isSubmitted;
    const allQuestionsAnswered = props.statistics.answeredQuestions === props.statistics.totalQuestions;
    const unansweredNonRequiredCount = props.statistics.totalQuestions - props.statistics.answeredQuestions - props.statistics.unansweredRequiredQuestions;

    const handleSubmit = async (): Promise<string> => {
        await props.onSubmit();
        return 'Questionnaire submitted.';
    };

    return (
        <div className={styles.reviewContainer}>
            {props.isSubmitted ? (
                <Text>The questionnaire has been submitted. Answers can no longer be updated, but discussion messages can still be sent.</Text>
            ) : props.cannotSubmitReason ? (
                <Text>{props.cannotSubmitReason}</Text>
            ) : (
                <>
                    {allQuestionsAnswered ? (
                        <Text>All questions have been answered. Once the questionnaire is submitted, {props.notifiedParty} will be notified and answers will no longer be editable.</Text>
                    ) : props.statistics.unansweredRequiredQuestions > 0 ? (
                        <Text color="red">
                            {props.statistics.unansweredRequiredQuestions} required {props.statistics.unansweredRequiredQuestions === 1 ? 'question' : 'questions'} must be answered before the questionnaire can be submitted.
                        </Text>
                    ) : (
                        <Text>
                            {unansweredNonRequiredCount} {unansweredNonRequiredCount === 1 ? 'question' : 'questions'} have not been answered. Once the questionnaire is submitted, {props.notifiedParty} will be notified and answers will no longer be editable.
                        </Text>
                    )}

                    {!props.isSubmitted && (
                        <div className={styles.submitButton}>
                            <Button variant="primary" onClick={() => setShowConfirmationModal(true)} disabled={!canSubmit} fontAwesomeImage={ICON_SUBMIT}>
                                Submit Questionnaire
                            </Button>
                        </div>
                    )}
                </>
            )}

            {showConfirmationModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Submit Questionnaire" areYouSureText="Are you sure you want to submit the questionnaire?" buttonText="Submit" buttonLoadingText="Submitting..." buttonIcon={ICON_SUBMIT} performOperation={handleSubmit} hideModal={() => setShowConfirmationModal(false)}>
                    <Text>{`Answers cannot be updated after the questionnaire is submitted. ${_.upperFirst(props.notifiedParty)} will be notified that the questionnaire is ready to be reviewed.`}</Text>
                </ConfirmationModal>
            )}
        </div>
    );
};
